import { notification } from 'antd';
import * as Sentry from "@sentry/browser";

/**
 * Internal function used to consistently format messages.
 * @param message 
 * @returns string
 */
const formatMessage = (
    message: string
): string => {
    let formattedMessage = message;
    if (formattedMessage[formattedMessage.length - 1] === '!') { // Remove exclamation marks from end
        formattedMessage = formattedMessage.slice(0, formattedMessage.length - 1);
    }
    if (formattedMessage[formattedMessage.length - 1] !== '.') { // Add period to end
        formattedMessage += '.';
    }
    return formattedMessage;
}

/**
 * Triggers a user-facing error box that displays the given message and disappears after a short time.
 * Filters out any error messages the user shouldn't see and formats the message for consistency.
 * Also sends the bug to Sentry for recording and debugging.
 * @param description The message to display in the error box
 * @returns void
 */
export const triggerErrorNotification = (
    description: string = '',
): void => {
    if (description.toLowerCase().includes('array key')) { // Filter out any invalid messages
        return;
    }
    const formattedMessage = formatMessage(description);
    if (!formattedMessage || !formattedMessage.length) {
        return;
    }
    
    notification.error({
        message: 'Error',
        description: formattedMessage,
    });
    Sentry.captureException(new Error(formattedMessage)); 
};

/**
 * Triggers an user-facing success box that displays the given message and disappears after a short time.
 * Formats the message for consistency.
 * @param description The message to display in the success box
 * @returns void
 */
export const triggerSuccessNotification = (
    description: string = '',
): void => {
    const formattedMessage = formatMessage(description);
    if (!formattedMessage || !formattedMessage.length) {
        return;
    }
    notification.success({
        message: 'Success',
        description: formattedMessage,
    });
};

/**
 * Triggers an user-facing warning box that displays the given message and disappears after a short time.
 * Formats the message for consistency.
 * @param description The message to display in the warning box
 * @returns void
 */
export const triggerWarningNotification = (
    description: string = '',
): void => {
    const formattedMessage = formatMessage(description);
    if (!formattedMessage || !formattedMessage.length) {
        return;
    }
    notification.warning({
        message: 'Warning',
        description: formattedMessage,
    });
}